<!-- eslint-disable import/no-duplicates -->
<template>
  <div class="flex flex-wrap w-full">
    <div
      v-if="!dataByMonth?.length && !data?.length && !rows?.length"
      class="w-full min-w-[100px] min-h-[240px] flex items-center justify-center"
    >
      <Icon
        :path="mdiLoading"
        class="text-neutral-light h-5 w-5 animate-spin"
      />
    </div>
    <AnalyticsScatterChart
      v-if="dataByMonth?.length"
      :data="dataByMonth"
      class="flex-shrink-0 w-full md:w-1/2"
      :dataset-label="
        $t(
          rentals
            ? 'project.statsLabels.avgRentalsPriceSqft'
            : 'project.statsLabels.avgSalesPriceSqft'
        )
      "
      :tick-formatter-function="formatterFunctionMonth"
      :tooltip-label-callback="context => getTooltip(context, true)"
      :get-bar-value="item => item.count"
      :bar-dataset-label="$t('project.statsLabels.soldUnits')"
      hide-x-axis-title
      smooth-lines
    />
    <AnalyticsScatterChart
      v-if="data?.length"
      :data="data"
      class="flex-shrink-0 w-full md:w-1/2"
      :dataset-label="
        $t(
          rentals
            ? 'project.statsLabels.avgRentalsPriceSqft'
            : 'project.statsLabels.avgSalesPriceSqft'
        )
      "
      :tick-formatter-function="formatterFunctionMonth"
      :tooltip-label-callback="context => getTooltip(context, false)"
      hide-x-axis-title
      hide-points
    />
    <AppTable
      v-if="rows?.length"
      class="mb-4"
      :header="header"
      container-class="table-container min-h-[200px] !mt-4"
      header-items-class="uppercase md:min-w-[86px] bg-blue-dark text-white text-sm"
      :values="rows"
      items-class="whitespace-nowrap"
      sortable
      :sort="{
        column: 'year',
        sort: 'asc'
      }"
    />
  </div>
</template>

<script setup lang="ts">
import { mdiLoading } from '@mdi/js';

import { GET_ANALYTICS_AVG_SALES_PRICE_SQFT } from '@/utils/queries';

const AnalyticsScatterChart = defineAsyncComponent(
  () => import('@/async-components/Analytics/ScatterChart.vue')
);
const AppTable = defineAsyncComponent(
  () => import('@/async-components/App/Table.vue')
);

const { t, n } = useI18n();

const { language } = useLanguage();

const props = defineProps({
  buildingName: {
    type: String,
    required: true
  },
  rentals: {
    type: Boolean,
    default: false
  }
});

const data = ref<
  {
    date: string;
    rawDate: number;
    value: number;
    buildingName: string;
    count: number;
  }[]
>([]);

function formatterFunctionMonth(d: Date) {
  d.setDate(d.getDate() + 15);
  return d.toLocaleDateString(language.value, {
    month: 'short',
    year: 'numeric'
  });
}

function formatterFunction(d: Date) {
  return d.toLocaleDateString(language.value, {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
}

const formatNumber = Intl.NumberFormat(language.value).format;

interface TooltipContext {
  chart: {
    data: {
      datasets: unknown[];
    };
  };
  dataIndex: number;
  dataset: { yAxisID: string };
  datasetIndex: number;
}

function getTooltip(context: TooltipContext, monthly: boolean) {
  const lines = [];

  const lastDataset =
    context.datasetIndex === context.chart.data.datasets.length - 1;

  const isBar = context.dataset.yAxisID === 'y1';

  const { value, rawDate, count } = (monthly ? dataByMonth.value : data.value)[
    context.dataIndex
  ];

  if (isBar) {
    if (count || count === 0) {
      lines.push(t('project.statsLabels.nSoldUnits', [formatNumber(count)]));
    }
  } else {
    const formattedPrice = props.rentals
      ? formatCurrencyDecimal(value)
      : n(value, 'currency', language.value);

    const label = props.rentals
      ? t('project.statsLabels.avgRentalsPriceSqft')
      : t('project.statsLabels.avgSalesPriceSqft');

    lines.push(`${label}: ${formattedPrice}`);
  }

  if (lastDataset) {
    const date = new Date(rawDate);

    lines.push(
      monthly ? formatterFunctionMonth(date) : formatterFunction(date)
    );

    // if (buildingName) {
    //   lines.push(buildingName);
    // }
  }

  return lines;
}

function formatCurrencyDecimal(v: number) {
  return Intl.NumberFormat(language.value).format(+v.toFixed(2)) + ' US$';
}

function formatCurrency(value: unknown) {
  if (typeof value !== 'number') {
    value = isNaN(+value) ? value : +value;
  }
  if (typeof value === 'number') {
    return n(value, 'currency', language.value);
  }
  return value;
}

const dataByMonth = ref<
  {
    date: string;
    rawDate: number;
    value: number;
    buildingName: string;
    count: number;
  }[]
>([]);

const header = computed(() => {
  // Year, Avg. Sales Price/Sqft, # Sold Units
  return [
    { key: 'year', label: t('project.statsLabels.year') },
    {
      key: 'price',
      label: props.rentals
        ? t('project.statsLabels.avgRentalsPriceSqft')
        : t('project.statsLabels.avgSalesPriceSqft'),
      format: props.rentals ? formatCurrencyDecimal : formatCurrency
    },
    {
      key: 'count',
      label: props.rentals
        ? t('project.statsLabels.rentedUnits')
        : t('project.statsLabels.soldUnits'),
      format: formatNumber
    }
  ];
});

const rows = ref<
  {
    year: string;
    price: number;
    count: number;
  }[]
>([]);

onMounted(() => {
  const client = useAnalyticsClient();

  client
    .query<{
      sold: {
        latest_updated_properties: {
          count: number;
          building_name: string;
          average_sales_price_sqft: number;
          close_date: {
            value: string;
          };
        };
      }[];
      soldByMonth: {
        latest_updated_properties: {
          count: number;
          building_name: string;
          average_sales_price_sqft: number;
          close_date: {
            month: string;
          };
        };
      }[];
      soldByYear: {
        latest_updated_properties: {
          count: number;
          average_sales_price_sqft: number;
          close_date: {
            year: string;
          };
        };
      }[];
    }>({
      query: GET_ANALYTICS_AVG_SALES_PRICE_SQFT,
      variables: {
        buildingName: props.buildingName,
        buyType: props.rentals ? 'RNT' : 'RES'
      }
    })
    .then(response => {
      if (!response.data.sold.length) {
        console.error('No data found for avg sales price sqft', response);
      }

      const processedData = response.data.sold
        .map(item => {
          const rawDate = new Date(
            item.latest_updated_properties.close_date.value
          );

          return {
            rawDate: +rawDate,
            date: rawDate.toLocaleDateString() || rawDate.toString(),
            value: item.latest_updated_properties.average_sales_price_sqft,
            count: item.latest_updated_properties.count,
            buildingName: item.latest_updated_properties.building_name
          };
        })
        .filter(item => item.rawDate);

      processedData.sort((a, b) => {
        return a.rawDate - b.rawDate;
      });

      const processedDataByMonth = response.data.soldByMonth
        .map(item => {
          const rawDate = new Date(
            item.latest_updated_properties.close_date.month
          );

          return {
            rawDate: +rawDate,
            date: rawDate.toLocaleDateString() || rawDate.toString(),
            value: item.latest_updated_properties.average_sales_price_sqft,
            count: item.latest_updated_properties.count,
            buildingName: item.latest_updated_properties.building_name
          };
        })
        .filter(item => item.rawDate);

      processedDataByMonth.sort((a, b) => {
        return a.rawDate - b.rawDate;
      });

      rows.value = response.data.soldByYear.map(item => {
        return {
          year: new Date(item.latest_updated_properties.close_date.year)
            .getFullYear()
            .toString(),
          price: item.latest_updated_properties.average_sales_price_sqft,
          count: item.latest_updated_properties.count
        };
      });

      data.value = processedData;

      dataByMonth.value = processedDataByMonth;
    });
});
</script>
